import React from 'react';
import styles from './LookalikeTable.module.scss';
import type { LookalikeRow } from '../../../store/investor/apiHelpers';
import clsx from 'clsx';
import { STATUS_COLORS, COLUMNS } from './constants';
import { useLookalikeTable } from './useLookalikeTable';
import type { StatusOption } from '../../../modules/Dashboard/DashboardModules/Overview/components/RelatedCompaniesNetwork/types';

interface LookalikeTableProps {
    source: LookalikeRow;
    lookalikes: LookalikeRow[];
    onRowClick: (orgId: string) => void;
    statusOptions?: StatusOption[];
    relationshipStatusOptions?: StatusOption[];
}

import SwapVertIcon from '../../../assets/swap_vert.svg';
import SwapVertUpIcon from '../../../assets/swap_vert_up.svg';
import SwapVertDownIcon from '../../../assets/swap_vert_down.svg';

const SortIcon: React.FC<{ direction?: 'asc' | 'desc' | null }> = ({ direction }) => {
    const isActive = !!direction;
    const src = direction === 'asc' ? SwapVertUpIcon : direction === 'desc' ? SwapVertDownIcon : SwapVertIcon;
    const alt = direction === 'asc' ? 'Sort ascending' : direction === 'desc' ? 'Sort descending' : 'Sort';
    const size = isActive ? 10 : 18;
    return (
        <span className={clsx(styles.sortIconWrap, isActive && styles.sortIconActive)}>
            <img src={src} alt={alt} width={size} height={size} />
        </span>
    );
};

const CompanyIcon: React.FC<{ color?: string }> = ({ color = '#6021b3' }) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={styles.companyIcon}>
        <path
            d="M2 14V3h5v2h5v9H2zm1-1h3V4H3v9zm4 0h3V6H7v7zm1-5h1v1H8V8zm0 2h1v1H8v-1zm-4-4h1v1H4V6zm0 2h1v1H4V8zm0 2h1v1H4v-1z"
            fill={color}
        />
    </svg>
);

const LookalikeTable: React.FC<LookalikeTableProps> = ({
    source,
    lookalikes,
    onRowClick,
    statusOptions = [],
    relationshipStatusOptions = [],
}) => {
    const { sortKey, sortDir, handleSort, sortedLookalikes, formatInvestors } = useLookalikeTable(lookalikes);

    const resolveLabel = (status: string | null, options: StatusOption[]): { label: string; bg?: string; color?: string } | null => {
        if (!status) return null;
        const match = options.find((opt) => opt.value?.toLowerCase() === status.toLowerCase());
        if (match) return { label: match.label, bg: match.backgroundColor, color: match.labelColor };
        return { label: status };
    };

    const renderStatus = (status: string | null, options: StatusOption[] = []) => {
        if (!status) return <span className={styles.naText}>NA</span>;
        const resolved = resolveLabel(status, options);
        const label = resolved?.label || status;
        const key = label.toLowerCase().trim();
        const fallbackStyle = STATUS_COLORS[key] || STATUS_COLORS[status.toLowerCase().trim()] || { bg: '#f3f4f6', color: '#374151' };
        const style = resolved?.bg ? { bg: resolved.bg, color: resolved.color || '#374151' } : fallbackStyle;
        return (
            <span className={styles.statusPill} style={{ backgroundColor: style.bg, color: style.color }}>
                {label}
            </span>
        );
    };

    const na = <span className={styles.naText}>NA</span>;
    const formatCurrency = (value: number | null) => (value != null ? `₹${Math.round(value)} Cr` : na);

    const renderCellValue = (row: LookalikeRow, key: string, isSource = false): React.ReactNode => {
        switch (key) {
            case 'similarityScore':
                if (isSource) return <span className={styles.naText}>—</span>;
                return <span className={styles.scoreCell}>{(row.similarityScore * 100).toFixed(0)}%</span>;
            case 'orgName':
                if (!row.orgName) return na;
                return (
                    <div className={styles.entityCell}>
                        <CompanyIcon color={isSource ? '#1f2937' : '#6021b3'} />
                        <span className={isSource ? styles.sourceEntityName : styles.entityName} title={row.orgName}>
                            {row.orgName}
                        </span>
                    </div>
                );
            case 'leadStatus':
                return renderStatus(row.leadStatus, statusOptions);
            case 'relationshipStatus':
                return renderStatus(row.relationshipStatus, relationshipStatusOptions);
            case 'revenue':
                return formatCurrency(row.revenue);
            case 'ebitdaPct':
                return row.ebitdaPct != null ? `${Math.round(row.ebitdaPct)}%` : na;
            case 'existingDebt':
                return formatCurrency(row.existingDebt);
            case 'equityRaised':
                return formatCurrency(row.equityRaised);
            case 'investors':
                return formatInvestors(row.investors);
            case 'state':
                return row.state ? <span className={styles.stateCell}>{row.state}</span> : na;
            default:
                return (row as any)[key] || na;
        }
    };

    return (
        <div className={styles.tableContainer}>
            <table className={styles.lookalikeTable}>
                <thead>
                    <tr>
                        {COLUMNS.map((col) => (
                            <th
                                key={col.key}
                                style={{ width: col.width }}
                                className={clsx(
                                    styles.sortableHeader,
                                    col.numeric && styles.numericHeader,
                                    col.key === 'orgName' && styles.stickyCol,
                                    col.key === 'state' && styles.stateCol,
                                )}
                                aria-sort={
                                    col.sortable && sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                                }
                            >
                                <button type="button" className={styles.sortButton} onClick={() => handleSort(col.key)}>
                                    <span className={styles.headerContent}>
                                        {col.label}
                                        <SortIcon direction={sortKey === col.key ? sortDir : null} />
                                    </span>
                                </button>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {/* Source company - fixed at top */}
                    <tr className={styles.sourceRow}>
                        {COLUMNS.map((col) => (
                            <td
                                key={col.key}
                                className={clsx(
                                    col.numeric && styles.numericCell,
                                    col.key === 'orgName' && styles.stickyCol,
                                    col.key === 'state' && styles.stateCol,
                                )}
                            >
                                {renderCellValue(source, col.key, true)}
                            </td>
                        ))}
                    </tr>

                    {/* Lookalike rows */}
                    {sortedLookalikes.map((row) => (
                        <tr
                            key={row.orgId}
                            className={styles.lookalikeRow}
                            onClick={() => onRowClick(row.orgId)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onRowClick(row.orgId);
                                }
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label={`View ${row.orgName || 'company'} details`}
                        >
                            {COLUMNS.map((col) => (
                                <td
                                    key={col.key}
                                    className={clsx(
                                        col.numeric && styles.numericCell,
                                        col.key === 'orgName' && styles.stickyCol,
                                        col.key === 'state' && styles.stateCol,
                                    )}
                                >
                                    {renderCellValue(row, col.key)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default LookalikeTable;
